import {
  collection,
  doc,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  getDocs,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Block, BlockRevision, BlockWithRevisions } from '@/types';

// ============================================
// Collection Path Helpers
// ============================================

const getBlocksRef = (userId: string) => collection(db, 'users', userId, 'blocks');

const getBlockRef = (userId: string, blockId: string) =>
  doc(db, 'users', userId, 'blocks', blockId);

const getRevisionsRef = (userId: string, blockId: string) =>
  collection(db, 'users', userId, 'blocks', blockId, 'revisions');

// ============================================
// Document Converters
// ============================================

export const docToBlock = (docSnap: QueryDocumentSnapshot<DocumentData>): Block => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId,
    date: data.date,
    startTime: data.startTime,
    duration: data.duration,
    title: data.title,
    notes: data.notes || '',
    categoryId: data.categoryId,
    color: data.color,
    status: data.status,
    linkedAlternativeId: data.linkedAlternativeId || null,
    isMIT: data.isMIT,
    mitOrder: data.mitOrder,
    originalDuration: data.originalDuration,
    durationChangedAt: data.durationChangedAt,
    missedReason: data.missedReason,
    missedAt: data.missedAt,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

const docToRevision = (docSnap: QueryDocumentSnapshot<DocumentData>): BlockRevision => ({
  id: docSnap.id,
  snapshot: docSnap.data().snapshot as Omit<Block, 'id'>,
  changedFields: docSnap.data().changedFields || [],
  createdAt: docSnap.data().createdAt,
});

// ============================================
// Input Types
// ============================================

export interface CreateBlockInput {
  date: string;
  startTime: number;
  duration: number;
  title: string;
  notes?: string;
  categoryId: string;
  color: string;
  status: 'planned' | 'completed' | 'cancelled';
}

// ============================================
// CRUD Operations
// ============================================

export async function createBlock(userId: string, data: CreateBlockInput): Promise<Block> {
  const now = Timestamp.now();
  const newBlockData = {
    userId,
    date: data.date,
    startTime: data.startTime,
    duration: data.duration,
    title: data.title.trim(),
    notes: data.notes?.trim() || '',
    categoryId: data.categoryId,
    color: data.color,
    status: data.status,
    linkedAlternativeId: null,
    originalDuration: data.duration,
    createdAt: now,
    updatedAt: now,
  };

  const blocksRef = getBlocksRef(userId);
  const docRef = await addDoc(blocksRef, newBlockData);

  return {
    id: docRef.id,
    ...newBlockData,
  };
}

export async function updateBlock(
  userId: string,
  blockId: string,
  updates: Partial<Block>
): Promise<void> {
  const blockRef = getBlockRef(userId, blockId);
  await updateDoc(blockRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBlock(userId: string, blockId: string): Promise<void> {
  const blockRef = getBlockRef(userId, blockId);
  await updateDoc(blockRef, {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// Queries
// ============================================

export async function getBlocksForDate(userId: string, date: string): Promise<Block[]> {
  const blocksRef = getBlocksRef(userId);
  const blocksQuery = query(blocksRef, where('date', '==', date), orderBy('startTime', 'asc'));

  const snapshot = await getDocs(blocksQuery);
  return snapshot.docs.map(docToBlock);
}

// ============================================
// Subscriptions
// ============================================

export function subscribeToBlocks(
  userId: string,
  date: string,
  onData: (blocks: BlockWithRevisions[]) => void,
  onError?: (error: Error) => void
): () => void {
  const blocksRef = getBlocksRef(userId);
  const blocksQuery = query(blocksRef, where('date', '==', date), orderBy('startTime', 'asc'));

  return onSnapshot(
    blocksQuery,
    async (snapshot) => {
      const blocksData: BlockWithRevisions[] = [];

      for (const docSnap of snapshot.docs) {
        const block = docToBlock(docSnap);

        // Fetch revisions for each block
        let revisions: BlockRevision[] = [];
        try {
          revisions = await getBlockRevisions(userId, docSnap.id);
        } catch (_e) {
          // Silently fail on revision fetch
          revisions = [];
        }

        blocksData.push({
          ...block,
          revisions,
        });
      }

      onData(blocksData);
    },
    (error) => {
      console.error('Error listening to blocks:', error);
      onError?.(error);
    }
  );
}

// ============================================
// Revisions
// ============================================

export async function createBlockRevision(
  userId: string,
  blockId: string,
  snapshot: Omit<Block, 'id'>,
  changedFields: string[]
): Promise<void> {
  // Build snapshot object, excluding undefined values (Firestore rejects undefined)
  const snapshotData: Record<string, unknown> = {
    userId: snapshot.userId,
    date: snapshot.date,
    startTime: snapshot.startTime,
    duration: snapshot.duration,
    title: snapshot.title,
    notes: snapshot.notes ?? '',
    categoryId: snapshot.categoryId,
    color: snapshot.color,
    status: snapshot.status,
  };

  // Only include optional fields if they have values
  if (snapshot.linkedAlternativeId !== undefined && snapshot.linkedAlternativeId !== null) {
    snapshotData.linkedAlternativeId = snapshot.linkedAlternativeId;
  }
  if (snapshot.originalDuration !== undefined) {
    snapshotData.originalDuration = snapshot.originalDuration;
  }
  if (snapshot.durationChangedAt !== undefined) {
    snapshotData.durationChangedAt = snapshot.durationChangedAt;
  }
  if (snapshot.createdAt !== undefined) {
    snapshotData.createdAt = snapshot.createdAt;
  }
  if (snapshot.updatedAt !== undefined) {
    snapshotData.updatedAt = snapshot.updatedAt;
  }

  const revisionsRef = getRevisionsRef(userId, blockId);
  await addDoc(revisionsRef, {
    snapshot: snapshotData,
    changedFields,
    createdAt: serverTimestamp(),
  });
}

export async function getBlockRevisions(userId: string, blockId: string): Promise<BlockRevision[]> {
  const revisionsRef = getRevisionsRef(userId, blockId);
  const revisionsQuery = query(revisionsRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(revisionsQuery);
  return snapshot.docs.map(docToRevision);
}
